import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../../service/ApiService';
import type { ScorecardSubmissionRequest } from '../../service/ApiService';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import {
  getVisibleQuestions,
  isScorecardComplete,
  type ScorecardAnswers,
  type PayerType,
} from './scorecardQuestions';
import InitiationShell from './InitiationShell';
import { documentLinkProps, isPlaceholderLink } from '../templateFile';
import './PocTypePage.scss';

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
};



/** FE answers → BE body. Missing/skipped → null (never false for skips). */
function toScorecardPayload(
  answers: ScorecardAnswers,
  payerType: PayerType | null
): ScorecardSubmissionRequest {
  const b = (code: string): boolean | null =>
    answers[code] === undefined ? null : Boolean(answers[code]);

  return {
    part1Question1: b('SC_Q1'),
    part1Question2: b('SC_Q2'),
    part1Question3: b('SC_Q3'),
    part1Question4: b('SC_Q4'),
    part1Question5: b('SC_Q5'),
    part1Question6: b('SC_Q6'),
    part1Question7: b('SC_Q7'),
    part1Question8: b('SC_Q8'),
    part1Question9: b('SC_Q9'),
    part1Question10: b('SC_Q10'),
    part1Question11: b('SC_Q11'),
    // Q12 is payer in UI; BE only stores a boolean (weight 0)
    part1Question12: payerType ? true : null,
    part1Question13: b('SC_Q13'),
    part1Question14: b('SC_Q14'),
    part1Question15: b('SC_Q15'),
    part2Question1: b('SC_O1'),
    part2Question2: b('SC_O2'),
    part2Question3: b('SC_O3'),
  };
}

function bandCopy(band: 'HIGHEST' | 'MEDIUM' | 'LOW') {
  if (band === 'HIGHEST') {
    return {
      bandTitle: 'Great!',
      bandMessage:
        'Please go ahead and move to PoC preparation. Your idea is well-aligned with market needs and has strong potential for success.',
    };
  }
  if (band === 'MEDIUM') {
    return {
      bandTitle: 'Promising!',
      bandMessage:
        'Consider refining your concept and addressing any potential weaknesses before moving forward. There is good potential, but some areas need improvement.',
    };
  }
  return {
    bandTitle: 'Needs Improvement',
    bandMessage:
      'Your scorecard is pretty low. Based on that value we recommend checking the low-achieved items and coming back.',
  };
}

const ScorecardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = (location.state as LocationState | null) ?? {};
  const pocId = incoming.pocId;
  const title =
    typeof incoming.businessJustification?.title === 'string'
      ? incoming.businessJustification.title
      : undefined;

  const [answers, setAnswers] = useState<ScorecardAnswers>({});
  const [payerType, setPayerType] = useState<PayerType | null>(null);
  const [attemptedNext, setAttemptedNext] = useState(false);

  const visible = useMemo(() => getVisibleQuestions(answers), [answers]);

  const sections = useMemo(() => {
    const grouped: { section: string; questions: typeof visible }[] = [];
    for (const q of visible) {
      const last = grouped[grouped.length - 1];
      if (!last || last.section !== q.section) {
        grouped.push({ section: q.section, questions: [q] });
      } else {
        last.questions.push(q);
      }
    }
    return grouped;
  }, [visible]);

  const setYesNo = (code: string, value: boolean) => {
    setAnswers((prev) => {
      const next: ScorecardAnswers = { ...prev, [code]: value };
      if (code === 'SC_Q6' && value === false) {
        delete next.SC_Q7;
        delete next.SC_Q8;
        delete next.SC_Q9;
        delete next.SC_Q10;
      }
      return next;
    });
    if (code === 'SC_Q11' && value === false) {
      setPayerType(null);
    }
  };

  const complete = isScorecardComplete(answers, payerType);
  const answeredCount = useMemo(
    () =>
      visible.filter((q) =>
        q.kind === 'payer' ? payerType != null : answers[q.code] !== undefined
      ).length,
    [visible, payerType, answers]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleNext = async () => {
    setAttemptedNext(true);
    setSubmitError(null);
    if (!complete) return;

    if (!pocId) {
      setSubmitError(
        'Missing PoC id. Go back to Step 1 and create the draft again.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const saved = await apiService.saveScorecard(
        pocId,
        toScorecardPayload(answers, payerType)
      );
      const copy = bandCopy(saved.band);

      navigate('/pocs/new/scorecard/result', {
        state: {
          ...incoming,
          scorecard: {
            answers,
            payerType,
            totalScore: saved.totalScore,
            band: saved.band,
            bandTitle: copy.bandTitle,
            bandMessage: copy.bandMessage,
            contactGdiRequired: saved.contactGdiRequired,
          },
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save scorecard. Check Network tab (often a skipped question sent as false).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <InitiationShell activeStep="step3">
      <Section theme="light" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Scorecard
          </Headline>
          <Lead>
            Step 3 — Answer each question with Yes or No.
            {title ? ` PoC: “${title}”.` : ''}
          </Lead>

          <div className="scorecard">
            {sections.map((group) => (
              <div key={group.section} className="scorecard__group">
                <h2 className="scorecard__section">{group.section}</h2>
                {group.questions.map((q) => (
                  <React.Fragment key={q.code}>
                    <div className="scorecard__row">
                      <div className="scorecard__prompt">
                        <p>
                          <span className="scorecard__num">{q.number}.</span>{' '}
                          {q.text}
                        </p>
                        {q.helpNote ? (
                          <p className="scorecard__help-note">{q.helpNote}</p>
                        ) : null}
                        {q.helpLinks?.length ? (
                          <div className="scorecard__links">
                            {q.helpLinks.map((link) =>
                              !isPlaceholderLink(link.href) ? (
                                <a
                                  key={link.label}
                                  className="scorecard__link"
                                  {...documentLinkProps(link.href)}
                                >
                                  {link.label}
                                </a>
                              ) : (
                                <button
                                  key={link.label}
                                  type="button"
                                  className="scorecard__link"
                                  disabled
                                  aria-disabled="true"
                                  title={`${link.label} — link coming soon`}
                                >
                                  {link.label}
                                  <span className="scorecard__link-soon">
                                    {' '}
                                    [coming soon]
                                  </span>
                                </button>
                              )
                            )}
                          </div>
                        ) : null}
                      </div>

                      {q.kind === 'yesno' ? (
                        <div
                          className="scorecard__answers"
                          role="group"
                          aria-label={q.text}
                        >
                          <button
                            type="button"
                            className={
                              'scorecard__yn' +
                              (answers[q.code] === true ? ' scorecard__yn--on' : '')
                            }
                            onClick={() => setYesNo(q.code, true)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={
                              'scorecard__yn' +
                              (answers[q.code] === false ? ' scorecard__yn--on' : '')
                            }
                            onClick={() => setYesNo(q.code, false)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div
                          className="scorecard__payer"
                          role="group"
                          aria-label={q.text}
                        >
                          <label className="bj-value">
                            <input
                              type="radio"
                              name="payerType"
                              checked={payerType === 'ISS_ENTITY'}
                              onChange={() => setPayerType('ISS_ENTITY')}
                            />
                            ISS entity
                          </label>
                          <label className="bj-value">
                            <input
                              type="radio"
                              name="payerType"
                              checked={payerType === 'CUSTOMER'}
                              onChange={() => setPayerType('CUSTOMER')}
                            />
                            Customer
                          </label>
                        </div>
                      )}
                    </div>

                    {q.code === 'SC_Q6' && answers.SC_Q6 === false ? (
                      <p className="scorecard__notice" role="status">
                        Please contact GDI for preferred-supplier guidance.
                        Questions about supplier fit and scalability are skipped.
                      </p>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>

          <p className="scorecard__progress">
            {answeredCount} of {visible.length} visible questions answered
          </p>

          {attemptedNext && !complete ? (
            <p className="bj-field__error">Please answer all visible questions.</p>
          ) : null}

          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <div className="initiation__actions">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/business-justification', { state: incoming })
              }
            >
              ← Back to Step 2
            </button>
            <button
              type="button"
              className="initiation__next"
              disabled={!complete || isSubmitting}
              onClick={handleNext}
            >
              {isSubmitting ? 'Saving…' : 'Next →'}
            </button>
          </div>
        </Section>
    </InitiationShell>
  );
};

export default ScorecardPage;
